import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { isCouponSendDue } from '@service/giftStore/page/giftCouponRedeem/giftCouponRedeem.utils';
import {
  giftCouponStatisticsDataAtom,
  redemptionDashboardDataAtom,
  redemptionDashboardStatusAtom,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  DeliverCouponDto,
  ExtraPaymentDto,
  UpdateDeliveredCouponFieldsDto,
  cancelReselectNotice,
  cancelSpawnedCoupon,
  deliverCoupons,
  fulfillFromExisting,
  getDonorCandidates,
  getPendingChargeEstimate,
  payExtraPayment,
  rejectCoupon,
  updateDeliveredCouponFields,
  updateRecipientPhone,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  giftCouponStatisticsUrl,
  redemptionDashboardUrl,
  useGiftCouponStatisticsHook,
  useRedemptionDashboardHook,
} from '@shared/api/giftCoupon/giftCoupon.hook';
import { getChannelBalances } from '@shared/api/giftChannelLedger/giftChannelLedger.controller';
import { ChannelBalanceSummary } from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import {
  DeliveryChannel,
  GiftCouponForRedemption,
  GiftCouponForRedemptionDashboard,
  GiftCouponRedeemStatus,
  PendingChargeEstimateChannel,
  PendingChargeEstimateItem,
  PendingChargeEstimateResponse,
  PhoneHistoryItem,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { useQueryClient } from 'react-query';

import {
  WholePriceBasis,
  computeListPriceEstimate,
  computeSelectedChargeEstimate,
  couponEstimateKey,
  couponListPrice,
  couponWholePrice,
} from '../utils/chargeEstimate.utils';
import {
  couponProductKey,
  couponUrlChannel,
  getBrandName,
  getCouponSaleRetail,
  getProductLabel,
  isOverIssued,
} from '../utils/redemption.utils';
import {
  executeUndeliver,
  executeUnhold,
  formatUndeliverToast,
} from '../utils/undeliver.utils';
import { useToast } from './useToast';

export interface DeliverModalState {
  couponIds: string[];
  priceRetail: number;
  priceWhole: string;
  /** 리베이트 — 입력 UI는 없앰(항상 '' → 0). 필요해지면 접었다 펴는 영역으로 되살릴 것 */
  priceRebate: string;
  /** 빈 문자열이면 API에 deliveryChannel 미전송 */
  deliveryChannel: DeliveryChannel | '';
  /** 도매가×건수 총액만큼 해당 매체에서 실제 발송했음을 확인했는지 */
  amountConfirmed: boolean;
  isBulk: boolean;
}

/** 발송 대상 상품 — 번호 복사·일괄 발송이 어떤 상품인지 화면에 못박기 위한 최소 정보 */
export interface RedemptionProductRef {
  key: string;
  label: string;
}

/**
 * 발주 상품의 예상 도매가 재료 — 상품이 고정됐으니 세 방식 단가를 그대로 꺼내 쓴다.
 * (1) 최근 발송가 기준: 백엔드 충전예측의 상품별 단가 (실제 직전 발송 건일 때만)
 * (2) 정가 감액 기준: 정가 × (1 − 매체 계약 감액률) — 감액률은 화면에서 선택 매체로 계산
 * (3) 등록 도매가: 선택상품(없으면 상품)에 적어둔 도매가 — 발송 이력이 없는 상품의 근거
 * 우선순위 정렬은 wholePriceSuggestions 에 있다.
 */
export interface RedemptionProductPricing extends RedemptionProductRef, WholePriceBasis {
  /** 매체 제안 ① 도매사이트 URL 기준 (addbiz→애드콘, officecon→오피스콘, biz.giftishow→기프티쇼) */
  urlChannel: DeliveryChannel | null;
  /** 매체 제안 ② 직전 발송 기준 — 예측이 실제 발송이력에서 뽑았을 때만 */
  recentDeliveredChannel: DeliveryChannel | null;
}

/**
 * 두 방식(URL·직전 발송)이 모두 있고 일치할 때만 확정 매체로 본다.
 * 이때만 발송처리 모달의 매체를 미리 채운다 — 근거 하나뿐이거나 엇갈리면 직접 고르게 한다.
 */
export function agreedDeliveryChannel(
  pricing: Pick<RedemptionProductPricing, 'urlChannel' | 'recentDeliveredChannel'>,
): DeliveryChannel | null {
  const { urlChannel, recentDeliveredChannel } = pricing;
  return urlChannel && urlChannel === recentDeliveredChannel ? urlChannel : null;
}

/** 번호 복사 확인 단계 — 클립보드에 넣기 직전 상품·예상 발주금액을 눈으로 확인시킨다 */
export interface CopyPhonesConfirmState extends RedemptionProductPricing {
  phones: string[];
  /** 번호가 없어 복사에서 빠지는 건수 */
  skippedCount: number;
}

/** 마지막으로 복사한 상품 — 발송처리 모달에서 상품 불일치 경고에 쓴다 */
export interface LastCopiedProduct extends RedemptionProductRef {
  count: number;
  at: number;
}

export interface HistoryModalState {
  phone: string;
  history: PhoneHistoryItem[];
}

export interface ShareHistoryModalState {
  fullCode: string;
  history: Array<{ sharedAt: Date; unsharedAt?: Date }>;
}

export function useRedemptionPage() {
  useRedemptionDashboardHook();
  useGiftCouponStatisticsHook();

  const queryClient = useQueryClient();
  const dashboardStatus = useRecoilValue(redemptionDashboardStatusAtom);
  const dashboard = useRecoilValue(redemptionDashboardDataAtom);
  const statistics = useRecoilValue(giftCouponStatisticsDataAtom);

  const allRequested = useMemo(() => {
    const list = dashboard?.requested ?? [];
    return [...list].sort((a, b) => {
      const dateA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const dateB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return dateB - dateA;
    });
  }, [dashboard]);

  // 추가결제 대기: 추가교환 spawn 쿠폰 중 아직 정산되지 않은 건.
  // 정산 = 추가결제 완료(extraPayment.isPaid / settledBy='extraPayment') 또는
  //        대체발송으로 재고 충당(settledBy='inventory'). 둘 중 하나면 발송대기로 이동.
  // (백엔드 발송가드와 동일 기준 — inventory 정산 건은 통과)
  const isExtraPaymentPending = (c: GiftCouponForRedemptionDashboard) =>
    c.isExtraPaid === true &&
    c.settledBy !== 'inventory' &&
    c.settledBy !== 'extraPayment' &&
    !c.extraPayment?.isPaid;

  const extraPaymentPending = useMemo(
    () => allRequested.filter(isExtraPaymentPending),
    [allRequested],
  );

  // 품절 재선택 안내 중(ReselectRequired): 수령자 응답을 기다리는 건이라 발송이 불가능하다.
  // 발송대기 목록·건수·충전 예상액에서 모두 빼고 별도 탭에서만 다룬다.
  // (백엔드 미배포 구서버는 이 상태가 아예 없어 빈 배열 — 화면도 자동으로 숨겨진다)
  const isReselectPending = (c: GiftCouponForRedemptionDashboard) =>
    c.redeemStatus === GiftCouponRedeemStatus.ReselectRequired;

  const sortByNoticeSentAt = (list: GiftCouponForRedemptionDashboard[]) =>
    [...list].sort((a, b) => {
      const at = a.reselect?.noticeSentAt
        ? new Date(a.reselect.noticeSentAt).getTime()
        : 0;
      const bt = b.reselect?.noticeSentAt
        ? new Date(b.reselect.noticeSentAt).getTime()
        : 0;
      return bt - at;
    });

  /** 안내 발송 후 수령자 응답 대기 중 */
  const reselectPending = useMemo(
    () =>
      sortByNoticeSentAt(
        allRequested.filter(
          (c) => isReselectPending(c) && c.reselect?.status !== 'CancelRequested',
        ),
      ),
    [allRequested],
  );

  /** 수령자가 선물을 거절(구매자 전액 환불 요청)한 건 — 관리자가 주문에서 미공유 처리로 마감 */
  const reselectCancelRequested = useMemo(
    () =>
      sortByNoticeSentAt(
        allRequested.filter(
          (c) => isReselectPending(c) && c.reselect?.status === 'CancelRequested',
        ),
      ),
    [allRequested],
  );

  // 발송보류(OnHold): 발송취소 시 운영자가 "나중에 처리"로 빼둔 건.
  // 재선택 안내와 같은 위상 — 발송대기 목록·건수·충전 예상액에서 모두 빠지고
  // 발송보류 탭에서만 다룬다. (구서버는 이 상태가 없어 항상 빈 배열)
  const isOnHold = (c: GiftCouponForRedemptionDashboard) =>
    c.redeemStatus === GiftCouponRedeemStatus.OnHold;

  /** 보류 최신순 — 오래 묵은 건이 아래로 내려가지 않게 보류 시각 기준 정렬 */
  const onHoldCoupons = useMemo(
    () =>
      [...allRequested.filter(isOnHold)].sort((a, b) => {
        const at = a.hold?.at ? new Date(a.hold.at).getTime() : 0;
        const bt = b.hold?.at ? new Date(b.hold.at).getTime() : 0;
        return bt - at;
      }),
    [allRequested],
  );

  // 발송대기(일반): 일반 Requested + 추가결제 완료된 스폰 쿠폰.
  const visibleRequested = useMemo(
    () =>
      allRequested.filter(
        (c) => !isExtraPaymentPending(c) && !isReselectPending(c) && !isOnHold(c),
      ),
    [allRequested],
  );

  const todayDelivered = useMemo(() => {
    const list = dashboard?.todayDelivered ?? [];
    return [...list].sort((a, b) => {
      const deliveredA = a.gifticon?.deliveredAt
        ? new Date(a.gifticon.deliveredAt).getTime()
        : 0;
      const deliveredB = b.gifticon?.deliveredAt
        ? new Date(b.gifticon.deliveredAt).getTime()
        : 0;
      if (deliveredA !== deliveredB) return deliveredB - deliveredA;
      const requestedA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const requestedB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return requestedA - requestedB;
    });
  }, [dashboard]);

  // 매체별 충전 필요액 예측 — 구서버(엔드포인트 미배포)면 404 → null 유지, 영역 미노출
  const [chargeEstimate, setChargeEstimate] =
    useState<PendingChargeEstimateResponse | null>(null);
  const estimateUnsupportedRef = useRef(false);

  const refreshChargeEstimate = useCallback(async () => {
    if (estimateUnsupportedRef.current) return;
    try {
      setChargeEstimate(await getPendingChargeEstimate());
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) {
        estimateUnsupportedRef.current = true; // 미배포 — 재시도 무의미
      }
      setChargeEstimate(null); // 예측은 보조 지표 — 실패해도 화면만 숨기고 조용히 넘어간다
    }
  }, []);

  useEffect(() => {
    refreshChargeEstimate();
  }, [refreshChargeEstimate]);

  // 채널 충전금 미러 잔액 — 예상 차감액과 나란히 보여 부족분을 미리 알린다.
  // 구서버(원장 미배포)면 404 → null 유지, 잔액 표기만 숨김
  const [channelBalances, setChannelBalances] = useState<ChannelBalanceSummary[] | null>(
    null,
  );
  const balancesUnsupportedRef = useRef(false);

  const refreshChannelBalances = useCallback(async () => {
    if (balancesUnsupportedRef.current) return;
    try {
      setChannelBalances(await getChannelBalances());
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) {
        balancesUnsupportedRef.current = true; // 미배포 — 재시도 무의미
      }
      setChannelBalances(null);
    }
  }, []);

  useEffect(() => {
    refreshChannelBalances();
  }, [refreshChannelBalances]);

  const invalidateDashboard = useCallback(() => {
    queryClient.invalidateQueries(redemptionDashboardUrl);
    queryClient.invalidateQueries(giftCouponStatisticsUrl);
    refreshChargeEstimate(); // 발송/정산 후 대기 구성이 바뀌므로 예상액도 갱신
    refreshChannelBalances(); // 발송 차감이 자동 기장되므로 미러 잔액도 갱신
  }, [queryClient, refreshChargeEstimate, refreshChannelBalances]);

  // Filters & selection
  const [productFilter, setProductFilterRaw] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [deliveringId, setDeliveringId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [showTodayDelivered, setShowTodayDelivered] = useState(false);

  // Modals
  const [deliverModal, setDeliverModal] = useState<DeliverModalState | null>(null);
  const [historyModal, setHistoryModal] = useState<HistoryModalState | null>(null);
  const [shareHistoryModal, setShareHistoryModal] =
    useState<ShareHistoryModalState | null>(null);

  // Toast — 공용 훅 사용 (자동 소멸 1200ms). 반환 키는 기존대로 toastMessage/showToast 유지.
  const {
    message: toastMessage,
    show: showToast,
    durationMs: toastDurationMs,
  } = useToast(1200);

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  // 상품 필터 변경 시 잘못된 일괄처리 방지를 위해 선택 초기화
  const setProductFilter = useCallback((next: string) => {
    setProductFilterRaw((prev) => {
      if (prev !== next) {
        setSelectedIds(new Set());
      }
      return next;
    });
  }, []);

  // 발송 슬롯(10/14/18시) 경계가 지나면 "지금 발송" 대상 수가 바뀌므로
  // 데이터 갱신이 없어도 1분마다 재계산되도록 틱을 둔다.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  // 정가 기준 모드 — 매체 계약 감액률(오피스콘 12%·애드콘 15%)로 다시 계산한 예상액.
  // 건수·상품 구성은 백엔드 예측 그대로 쓰고 정가만 대기 쿠폰에서 가져온다.
  const listPriceEstimate = useMemo(
    () =>
      chargeEstimate ? computeListPriceEstimate(chargeEstimate, visibleRequested) : null,
    [chargeEstimate, visibleRequested],
  );

  // 상품키별 예측 단가 — 상품별 대기 목록의 예상 금액 계산에 재사용 (null=예측 미지원)
  const estimateUnitPriceByKey = useMemo(() => {
    if (!chargeEstimate) return null;
    const map = new Map<string, number>();
    for (const bucket of chargeEstimate.channels) {
      for (const item of bucket.items) {
        map.set(item.key, item.unitPriceWhole);
      }
    }
    return map;
  }, [chargeEstimate]);

  // Product filter options (with duplicate count, oldest request time & due count)
  const productOptions = useMemo(() => {
    const now = new Date(nowTick);
    const optionMap = new Map<
      string,
      {
        key: string;
        label: string;
        itemName: string;
        brandName: string | null;
        unitName: string | null;
        count: number;
        duplicateCount: number;
        dueCount: number;
        oldestRequestedAt: number;
        requestedAtSum: number;
        requestedAtCount: number;
        estimatedAmount: number;
        estimatedListAmount: number;
      }
    >();

    for (const coupon of visibleRequested) {
      // 상품키는 선택 잠금·필터와 같은 규칙을 써야 한다 (couponProductKey 단일 출처)
      const key = couponProductKey(coupon);
      if (!key) continue;

      let itemName: string;
      let unitName: string | null = null;
      // 브랜드는 populate 여부에 따라 없을 수 있어 항상 옵셔널 취급
      const brandName = getBrandName(coupon);

      if (key.startsWith('unit:')) {
        itemName =
          typeof coupon.giftItem === 'object'
            ? coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || ''
            : '';
        unitName =
          coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object'
            ? coupon.selectedChoiceUnit.name
            : null;
      } else {
        itemName =
          typeof coupon.giftItem === 'object'
            ? coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-'
            : '-';
      }

      // 상품명 - 브랜드 - 선택유닛명 (select 옵션 등 평문 표기용)
      const label = [itemName, brandName, unitName].filter(Boolean).join(' - ') || '-';

      const isDuplicate = !!(coupon as GiftCouponForRedemptionDashboard).isDuplicatePhone;
      const requestedAt = coupon.recipient?.requestedAt
        ? new Date(coupon.recipient.requestedAt).getTime()
        : Infinity;
      const hasValidTime = isFinite(requestedAt);
      const isDue = hasValidTime && isCouponSendDue(requestedAt, now);
      // 상품별 예상 금액 — 필터 키(item:)와 예측 키(info:)가 갈릴 수 있어 쿠폰 단위로 매핑
      const estimateKey = couponEstimateKey(coupon);
      const unitPrice = estimateUnitPriceByKey?.get(estimateKey) ?? 0;
      // 정가 기준 단가 — 정가·감액률이 없으면 기존 단가로 폴백(총액 비교 가능하게)
      const unitListPrice =
        listPriceEstimate?.byKey.get(estimateKey)?.unitPrice ?? unitPrice;

      const existing = optionMap.get(key);
      if (existing) {
        existing.count++;
        if (isDuplicate) existing.duplicateCount++;
        if (isDue) existing.dueCount++;
        if (requestedAt < existing.oldestRequestedAt) {
          existing.oldestRequestedAt = requestedAt;
        }
        if (hasValidTime) {
          existing.requestedAtSum += requestedAt;
          existing.requestedAtCount++;
        }
        existing.estimatedAmount += unitPrice;
        existing.estimatedListAmount += unitListPrice;
      } else {
        optionMap.set(key, {
          key,
          label,
          itemName,
          brandName,
          unitName,
          count: 1,
          duplicateCount: isDuplicate ? 1 : 0,
          dueCount: isDue ? 1 : 0,
          oldestRequestedAt: requestedAt,
          requestedAtSum: hasValidTime ? requestedAt : 0,
          requestedAtCount: hasValidTime ? 1 : 0,
          estimatedAmount: unitPrice,
          estimatedListAmount: unitListPrice,
        });
      }
    }

    return Array.from(optionMap.values())
      .map((v) => ({
        key: v.key,
        label: v.label,
        itemName: v.itemName,
        brandName: v.brandName,
        unitName: v.unitName,
        count: v.count,
        duplicateCount: v.duplicateCount,
        dueCount: v.dueCount,
        oldestRequestedAt: v.oldestRequestedAt,
        avgRequestedAt:
          v.requestedAtCount > 0
            ? Math.round(v.requestedAtSum / v.requestedAtCount)
            : Infinity,
        estimatedAmount: estimateUnitPriceByKey ? v.estimatedAmount : undefined,
        estimatedListAmount: listPriceEstimate ? v.estimatedListAmount : undefined,
      }))
      .sort((a, b) => a.oldestRequestedAt - b.oldestRequestedAt);
  }, [visibleRequested, nowTick, estimateUnitPriceByKey, listPriceEstimate]);

  // Filtered data
  const filteredData = useMemo(() => {
    let result = [...visibleRequested];

    // 상품키 완전일치 — item: 필터에 같은 상품의 선택유닛 쿠폰이 섞여 들어오지 않게
    // (상품별 대기 칩의 건수와 목록 건수가 항상 일치한다)
    if (productFilter !== 'all') {
      result = result.filter((item) => couponProductKey(item) === productFilter);
    }

    if (searchText) {
      const search = searchText.toLowerCase().replace(/-/g, '');
      result = result.filter((item) => {
        const phone = item.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const itemName =
          typeof item.giftItem === 'object'
            ? (
                item.giftItem?.name ||
                item.giftItem?.giftItemInfo?.name ||
                ''
              ).toLowerCase()
            : '';
        const unitName =
          item.selectedChoiceUnit && typeof item.selectedChoiceUnit === 'object'
            ? item.selectedChoiceUnit.name?.toLowerCase() || ''
            : '';
        const fullCode = (item.fullCode || '').toLowerCase();
        const order = typeof item.giftOrder === 'object' ? item.giftOrder : null;
        const ordererPhone = (order?.bugo?.member?.phoneNumber || '').replace(/-/g, '');
        return (
          phone.includes(search) ||
          itemName.includes(search) ||
          unitName.includes(search) ||
          fullCode.includes(search) ||
          ordererPhone.includes(search)
        );
      });
    }

    result.sort((a, b) => {
      const dateA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const dateB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return dateB - dateA;
    });

    return result;
  }, [visibleRequested, productFilter, searchText]);

  // Selection
  // 선택 잠금 — 선택은 항상 "한 상품"만 담는다. 첫 선택이 상품을 고정하고,
  // 다른 상품 건은 선택 자체가 막힌다. (섞인 선택 → 번호 통복사 → 매체에서
  // 엉뚱한 상품으로 일괄 구매되는 사고 방지)
  const selectedProduct = useMemo(() => {
    if (selectedIds.size === 0) return null;
    for (const coupon of visibleRequested) {
      if (!selectedIds.has(coupon._id)) continue;
      const key = couponProductKey(coupon);
      if (!key) continue;
      return { key, label: getProductLabel(coupon) };
    }
    return null;
  }, [visibleRequested, selectedIds]);

  // 전체선택은 상품 필터가 걸렸을 때만 (전체 상품 화면에서는 일괄 클릭 자체를 없앤다)
  const canToggleAll = productFilter !== 'all';

  const isAllSelected = useMemo(
    () =>
      canToggleAll &&
      filteredData.length > 0 &&
      filteredData.every((item) => selectedIds.has(item._id)),
    [canToggleAll, filteredData, selectedIds],
  );

  // 초과발송(구매 수량 초과) 건 강제 발송 모드 — 관리자 시크릿 키로 잠금 해제.
  // 켜지면 단건/일괄 발송에서 초과발송 차단을 우회한다. (admin 손실 감수 예외 발송)
  const [allowOverIssue, setAllowOverIssue] = useState(false);

  const requestOverIssueOverride = useCallback(() => {
    if (allowOverIssue) return;
    const expected = process.env.REACT_APP_GIFT_STORE_ADMIN_PREVIEW_KEY ?? '';
    const input = window.prompt(
      '초과발송(구매 수량 초과) 건을 강제로 발송하려면 관리자 시크릿 키를 입력하세요.\n(admin 손실이 발생할 수 있는 예외 발송입니다.)',
    );
    if (input == null) return; // 취소
    if (!expected || input.trim() !== expected) {
      alert('시크릿 키가 일치하지 않습니다.');
      return;
    }
    setAllowOverIssue(true);
  }, [allowOverIssue]);

  // 선택된 쿠폰 중 초과발송(위험) 건의 ID 목록 - 일괄발송 차단용
  const overIssuedSelectedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const coupon of visibleRequested) {
      if (selectedIds.has(coupon._id) && isOverIssued(coupon)) {
        ids.add(coupon._id);
      }
    }
    return ids;
  }, [visibleRequested, selectedIds]);

  // 선택분 충전 예상 — 전체 예측의 상품별 단가를 선택 쿠폰에 재적용 (백엔드 재호출 없음)
  const selectedChargeEstimate = useMemo(() => {
    if (!chargeEstimate || selectedIds.size === 0) return null;
    return computeSelectedChargeEstimate(
      chargeEstimate,
      visibleRequested.filter((c) => selectedIds.has(c._id)),
      listPriceEstimate?.byKey,
    );
  }, [chargeEstimate, selectedIds, visibleRequested, listPriceEstimate]);

  // 선택 영역에서 초과발송 건만 일괄 해제
  const deselectOverIssued = useCallback(() => {
    if (overIssuedSelectedIds.size === 0) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      overIssuedSelectedIds.forEach((id) => next.delete(id));
      return next;
    });
  }, [overIssuedSelectedIds]);

  const handleToggleAll = useCallback(() => {
    // 전체 상품 화면에서는 일괄 선택 금지 — 상품을 먼저 고르게 한다 (UI에서도 미노출)
    if (!canToggleAll) {
      showToast('상품을 먼저 선택해야 일괄 선택할 수 있습니다.');
      return;
    }
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredData.map((item) => item._id)));
    }
  }, [canToggleAll, isAllSelected, filteredData, showToast]);

  const handleToggleSelect = useCallback(
    (couponId: string) => {
      // 해제는 항상 허용, 추가는 잠긴 상품과 같을 때만 허용
      if (!selectedIds.has(couponId) && selectedProduct) {
        const target = visibleRequested.find((c) => c._id === couponId);
        const key = target ? couponProductKey(target) : null;
        if (key !== selectedProduct.key) {
          showToast(
            `다른 상품은 함께 선택할 수 없습니다. (선택 중: ${selectedProduct.label})`,
          );
          return;
        }
      }
      setSelectedIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(couponId)) {
          newSet.delete(couponId);
        } else {
          newSet.add(couponId);
        }
        return newSet;
      });
    },
    [selectedIds, selectedProduct, visibleRequested, showToast],
  );

  // ── 번호 복사 (매체 발주용) ──────────────────────────────────────────
  // 복사한 번호는 그대로 발송매체에 붙여넣어 "한 상품"으로 일괄 구매된다.
  // 되돌릴 수 없는 실제 구매라 (1) 상품이 섞이면 복사 자체를 막고,
  // (2) 클립보드에 넣기 직전 어떤 상품인지 확인시키고,
  // (3) 복사한 상품을 기억해 발송처리 때 다른 상품이면 경고한다.
  const [copyConfirm, setCopyConfirm] = useState<CopyPhonesConfirmState | null>(null);
  const [lastCopiedProduct, setLastCopiedProduct] = useState<LastCopiedProduct | null>(
    null,
  );

  // 예측 상품키 → 최근 발송가·매체·근거 (충전예측 응답 평탄화 — 단가·매체 제안에 재사용)
  const estimateRefByKey = useMemo(() => {
    const map = new Map<
      string,
      {
        channel: PendingChargeEstimateChannel;
        unitPriceWhole: number;
        priceSource: PendingChargeEstimateItem['priceSource'];
        channelSource: PendingChargeEstimateItem['channelSource'];
      }
    >();
    if (!chargeEstimate) return map;
    for (const bucket of chargeEstimate.channels) {
      for (const item of bucket.items) {
        map.set(item.key, {
          channel: bucket.channel,
          unitPriceWhole: item.unitPriceWhole,
          priceSource: item.priceSource,
          channelSource: item.channelSource,
        });
      }
    }
    return map;
  }, [chargeEstimate]);

  /**
   * 상품이 고정된 뒤 쓰는 발주 제안 재료.
   * 도매가 = 최근 발송가 / 정가 감액 / 등록 도매가, 매체 = 도매사이트 URL / 직전 발송.
   */
  const productPricingOf = useCallback(
    (coupon: GiftCouponForRedemption): RedemptionProductPricing => {
      const ref = estimateRefByKey.get(couponEstimateKey(coupon));
      // 예측 단가는 이력이 없으면 등록 도매가로 폴백돼 오는데(priceSource choiceUnit/giftItem),
      // 그건 아래 wholePrice 가 쿠폰 자신의 값으로 더 정확히 잡으므로 실발송 건만 최근가로 본다
      const whole = couponWholePrice(coupon);
      return {
        key: couponProductKey(coupon) ?? 'unknown',
        label: getProductLabel(coupon),
        recentUnitPrice:
          ref?.priceSource === 'recentDelivered' ? ref.unitPriceWhole : null,
        recentChannel: ref?.channel ?? null,
        listPrice: couponListPrice(coupon),
        wholePrice: whole?.price ?? null,
        wholePriceSource: whole?.source ?? null,
        urlChannel: couponUrlChannel(coupon),
        // 예측이 URL로 매체를 정한 경우는 URL 제안과 같은 근거라 직전 발송 신호가 아니다
        recentDeliveredChannel:
          ref?.channelSource === 'recentDelivered' ? ref.channel : null,
      };
    },
    [estimateRefByKey],
  );

  const requestCopySelectedPhones = useCallback(() => {
    const selected = visibleRequested.filter((c) => selectedIds.has(c._id));
    const first = selected[0];
    if (!first) {
      showToast('선택된 항목이 없습니다.');
      return;
    }
    // 선택 잠금이 있어 정상 경로에서는 걸리지 않는 방어선
    const keys = new Set(selected.map((c) => couponProductKey(c) ?? 'unknown'));
    if (keys.size > 1) {
      showToast('상품이 섞여 있어 번호를 복사할 수 없습니다. 상품별로 나눠주세요.');
      return;
    }
    const phones = selected
      .map((c) => c.recipient?.phoneNumber)
      .filter((p): p is string => !!p)
      .map((p) => p.replace(/[^0-9]/g, ''))
      .filter((p) => p.length > 0);
    if (phones.length === 0) {
      showToast('복사할 번호가 없습니다.');
      return;
    }
    setCopyConfirm({
      ...productPricingOf(first),
      phones,
      skippedCount: selected.length - phones.length,
    });
  }, [visibleRequested, selectedIds, showToast, productPricingOf]);

  const confirmCopySelectedPhones = useCallback(() => {
    if (!copyConfirm) return;
    navigator.clipboard.writeText(copyConfirm.phones.join('\n'));
    setLastCopiedProduct({
      key: copyConfirm.key,
      label: copyConfirm.label,
      count: copyConfirm.phones.length,
      at: Date.now(),
    });
    setCopyConfirm(null);
    showToast(`${copyConfirm.label} ${copyConfirm.phones.length}건 복사됨`);
  }, [copyConfirm, showToast]);

  /**
   * 발송처리 모달이 쿠폰을 찾는 범위 — 발송대기 + **발송보류**.
   * 보류는 대기 큐에서 빼두는 것이 목적이지 발송 금지가 아니다. 다른 경로로 매체
   * 발송에 성공하면 보류 탭에서 바로 발송처리할 수 있어야 한다(서버도 OnHold 발송 허용).
   * 선택·집계·충전예상액은 그대로 visibleRequested만 쓴다.
   */
  const deliverablePool = useMemo(
    () => [...visibleRequested, ...onHoldCoupons],
    [visibleRequested, onHoldCoupons],
  );

  // 발송처리 대상 상품 — 마지막 복사 상품과 대조해 오발주를 잡고, 예상 도매가를 제안한다
  const deliverProduct = useMemo((): RedemptionProductPricing | null => {
    const firstId = deliverModal?.couponIds[0];
    if (!firstId) return null;
    const coupon = deliverablePool.find((c) => c._id === firstId);
    if (!coupon) return null;
    return productPricingOf(coupon);
  }, [deliverModal, deliverablePool, productPricingOf]);

  // Deliver modal
  const openDeliverModal = useCallback(
    (couponId: string) => {
      const target = deliverablePool.find((c) => c._id === couponId);
      if (!allowOverIssue && target && isOverIssued(target)) {
        alert(
          '구매 수량을 초과한 위험 쿠폰입니다.\n발송할 수 없습니다. 본사/관리자에 확인해주세요.',
        );
        return;
      }
      const priceRetail = getCouponSaleRetail(couponId, deliverablePool);
      setDeliverModal({
        couponIds: [couponId],
        priceRetail,
        priceWhole: '',
        priceRebate: '',
        // URL·직전 발송 두 근거가 일치할 때만 미리 채운다 (엇갈리면 직접 선택)
        deliveryChannel: target
          ? agreedDeliveryChannel(productPricingOf(target)) ?? ''
          : '',
        amountConfirmed: false,
        isBulk: false,
      });
    },
    [deliverablePool, allowOverIssue, productPricingOf],
  );

  const openBulkDeliverModal = useCallback(() => {
    const targetIds = Array.from(selectedIds);
    if (targetIds.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }
    if (!allowOverIssue && overIssuedSelectedIds.size > 0) {
      alert(
        `선택 항목에 초과발송 의심 건이 ${overIssuedSelectedIds.size}건 포함되어 있어 일괄 발송할 수 없습니다.\n해당 건들을 선택 해제한 뒤 다시 시도해주세요.`,
      );
      return;
    }
    const firstId = targetIds[0];
    if (!firstId) return;
    const priceRetail = getCouponSaleRetail(firstId, visibleRequested);
    // 선택은 한 상품으로 고정돼 있어 첫 건의 매체 근거가 선택 전체에 그대로 적용된다
    const first = visibleRequested.find((c) => c._id === firstId);
    setDeliverModal({
      couponIds: targetIds,
      priceRetail,
      priceWhole: '',
      priceRebate: '',
      deliveryChannel: first ? agreedDeliveryChannel(productPricingOf(first)) ?? '' : '',
      amountConfirmed: false,
      isBulk: true,
    });
  }, [
    selectedIds,
    visibleRequested,
    overIssuedSelectedIds,
    allowOverIssue,
    productPricingOf,
  ]);

  const handleConfirmDeliver = useCallback(async () => {
    if (!deliverModal) return;

    const priceWhole = Number(deliverModal.priceWhole);
    // 리베이트는 입력 UI가 없어 항상 0 — 되살릴 때를 위해 파싱·검증은 남겨둔다
    const priceRebate =
      deliverModal.priceRebate.trim() === '' ? 0 : Number(deliverModal.priceRebate);

    if (!deliverModal.deliveryChannel) {
      alert('발송 매체를 선택해주세요.\n매체를 선택하지 않으면 발송 처리할 수 없습니다.');
      return;
    }
    if (!deliverModal.priceWhole || isNaN(priceWhole)) {
      alert('도매가를 입력해주세요.');
      return;
    }
    if (isNaN(priceRebate)) {
      alert('리베이트는 숫자로 입력해주세요.');
      return;
    }
    if (!deliverModal.amountConfirmed) {
      const total = priceWhole * deliverModal.couponIds.length;
      alert(`총 발주비용(${total.toLocaleString()}원)이 맞는지 확인 후 체크해주세요.`);
      return;
    }

    const dto: DeliverCouponDto = {
      priceWhole,
      priceRetail: deliverModal.priceRetail,
      priceRebate,
      deliveryChannel: deliverModal.deliveryChannel,
    };

    if (deliverModal.isBulk) {
      setIsBulkProcessing(true);
      setDeliverModal(null);

      try {
        await deliverCoupons(deliverModal.couponIds, dto);
        alert(`${deliverModal.couponIds.length}건 발송 처리 완료`);
      } catch (error) {
        console.error('일괄 발송 처리 실패:', error);
        alert('일괄 발송 처리에 실패했습니다.');
      } finally {
        setIsBulkProcessing(false);
        setSelectedIds(new Set());
        invalidateDashboard();
      }
    } else {
      const couponId = deliverModal.couponIds[0] ?? '';
      setDeliveringId(couponId);
      setDeliverModal(null);

      try {
        await deliverCoupons([couponId], dto);
        invalidateDashboard();
        alert('발송 처리가 완료되었습니다.');
      } catch (error) {
        console.error('발송 처리 실패:', error);
        alert('발송 처리에 실패했습니다.');
      } finally {
        setDeliveringId(null);
      }
    }
  }, [deliverModal, invalidateDashboard]);

  const handleReject = useCallback(
    async (couponId: string) => {
      if (
        !confirm('이 쿠폰을 발송불가 처리하시겠습니까?\n(수신자 번호가 잘못된 경우 등)')
      ) {
        return;
      }
      try {
        await rejectCoupon(couponId);
        invalidateDashboard();
        alert('발송불가 처리가 완료되었습니다.');
      } catch (error) {
        console.error('발송불가 처리 실패:', error);
        alert('발송불가 처리에 실패했습니다.');
      }
    },
    [invalidateDashboard],
  );

  const handleUpdateRecipientPhone = useCallback(
    async (couponId: string, newPhone: string) => {
      const cleaned = newPhone.replace(/[^0-9]/g, '');
      if (cleaned.length < 10 || cleaned.length > 11) {
        alert('올바른 전화번호를 입력해주세요.');
        return false;
      }
      try {
        await updateRecipientPhone(couponId, cleaned);
        invalidateDashboard();
        showToast('수신자 번호가 변경되었습니다.');
        return true;
      } catch (error) {
        console.error('수신자 번호 변경 실패:', error);
        alert('수신자 번호 변경에 실패했습니다.');
        return false;
      }
    },
    [invalidateDashboard, showToast],
  );

  const openHistoryModal = useCallback((coupon: GiftCouponForRedemption) => {
    const dc = coupon as GiftCouponForRedemptionDashboard;
    if (dc.phoneHistory && dc.phoneHistory.length > 0) {
      setHistoryModal({
        phone: coupon.recipient?.phoneNumber || '',
        history: dc.phoneHistory,
      });
    }
  }, []);

  const openShareHistoryModal = useCallback((coupon: GiftCouponForRedemption) => {
    if (coupon.shareHistory && coupon.shareHistory.length > 0) {
      setShareHistoryModal({
        fullCode: coupon.fullCode || coupon._id,
        history: coupon.shareHistory,
      });
    }
  }, []);

  const handleBulkUpdateDeliveredCoupon = useCallback(
    async (couponIds: string[], dto: UpdateDeliveredCouponFieldsDto) => {
      if (couponIds.length === 0) return;
      if (dto.deliveryChannel === undefined && dto.priceWhole === undefined) return;

      const parts: string[] = [];
      if (dto.priceWhole !== undefined) {
        parts.push(`도매가: ${dto.priceWhole.toLocaleString()}원`);
      }
      if (dto.deliveryChannel !== undefined) {
        parts.push(`매체: ${dto.deliveryChannel || '입력안함'}`);
      }
      if (
        !confirm(`${couponIds.length}건을 다음과 같이 수정합니다.\n${parts.join('\n')}`)
      ) {
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const id of couponIds) {
        try {
          await updateDeliveredCouponFields(id, dto);
          successCount++;
        } catch (error) {
          console.error(`쿠폰 수정 실패 (${id}):`, error);
          failCount++;
        }
      }

      invalidateDashboard();

      if (failCount === 0) {
        showToast(`${successCount}건 수정 완료`);
      } else {
        alert(`성공: ${successCount}건, 실패: ${failCount}건`);
      }
    },
    [invalidateDashboard, showToast],
  );

  // 발송완료 취소 — 발송대기(또는 발송보류)로 복귀 + 원장 자동 환급
  // (스펙: docs/gift-coupon-undeliver-spec.md · docs/gift-coupon-onhold-spec.md)
  const handleBulkUndeliverCoupon = useCallback(
    async (couponIds: string[], reason: string, hold = false) => {
      const result = await executeUndeliver(couponIds, reason, hold);
      if (!result) return false;
      // invalidateDashboard가 대기목록·통계·충전예상액·원장 잔액까지 모두 갱신한다
      invalidateDashboard();
      showToast(formatUndeliverToast(result));
      return true;
    },
    [invalidateDashboard, showToast],
  );

  // 추가결제 대기 — (A) 추가결제 후 발송
  const handlePayExtraPayment = useCallback(
    async (couponId: string, dto: ExtraPaymentDto) => {
      try {
        await payExtraPayment(couponId, dto);
        invalidateDashboard();
        showToast('추가결제 처리 완료 — 발송대기로 이동했습니다.');
        return true;
      } catch (error) {
        console.error('추가결제 처리 실패:', error);
        alert(error instanceof Error ? error.message : '추가결제 처리에 실패했습니다.');
        return false;
      }
    },
    [invalidateDashboard, showToast],
  );

  // 추가결제 대기 — (B) 기구매 미전달 쿠폰으로 대체발송
  const handleFulfillFromExisting = useCallback(
    async (couponId: string, donorCouponId: string) => {
      try {
        await fulfillFromExisting(couponId, donorCouponId);
        invalidateDashboard();
        showToast('대체발송 처리 완료 — 발송대기로 이동했습니다.');
        return true;
      } catch (error) {
        console.error('대체발송 처리 실패:', error);
        alert(error instanceof Error ? error.message : '대체발송 처리에 실패했습니다.');
        return false;
      }
    },
    [invalidateDashboard, showToast],
  );

  // 추가결제 대기 — (C) 신청 취소 (스폰 쿠폰 hard-delete + 부수효과 되돌리기)
  const handleCancelSpawned = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '이 추가결제 대기 신청을 취소하시겠습니까?\n신청 쿠폰이 삭제되고 원본 쿠폰의 추가교환 허용 슬롯이 복구됩니다.\n(실제 결제된 건은 취소할 수 없으며 환불로 처리해야 합니다.)',
        )
      ) {
        return false;
      }
      try {
        await cancelSpawnedCoupon(couponId);
        invalidateDashboard();
        showToast('신청이 취소되었습니다.');
        return true;
      } catch (error) {
        console.error('신청 취소 실패:', error);
        alert(error instanceof Error ? error.message : '신청 취소에 실패했습니다.');
        return false;
      }
    },
    [invalidateDashboard, showToast],
  );

  // 발송보류 해제 — 다시 발송대기로 (스펙: docs/gift-coupon-onhold-spec.md)
  const handleUnholdCoupons = useCallback(
    async (couponIds: string[]) => {
      if (couponIds.length === 0) return false;
      if (
        !confirm(
          `보류 ${couponIds.length}건을 발송대기로 되돌리시겠습니까?\n(신청정보는 그대로이며 바로 발송 가능해집니다)`,
        )
      ) {
        return false;
      }
      const unheld = await executeUnhold(couponIds);
      if (unheld === null) return false;
      invalidateDashboard();
      showToast(`발송대기로 복귀 ${unheld}건`);
      return true;
    },
    [invalidateDashboard, showToast],
  );

  // 품절 안내 철회 — 오처리 복구 (ReselectRequired → Requested)
  const handleCancelReselectNotice = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '이 건의 품절 안내를 취소하시겠습니까?\n발송대기로 되돌아가며, 수령자 화면의 재선택 링크는 동작하지 않게 됩니다.',
        )
      ) {
        return false;
      }
      try {
        await cancelReselectNotice(couponId);
        invalidateDashboard();
        showToast('품절 안내를 취소했습니다.');
        return true;
      } catch (error) {
        console.error('품절 안내 취소 실패:', error);
        alert(
          (error as { response?: { status?: number } })?.response?.status === 404
            ? '서버에 아직 반영되지 않은 기능입니다.'
            : '품절 안내 취소에 실패했습니다.',
        );
        return false;
      }
    },
    [invalidateDashboard, showToast],
  );

  // 품절 안내 발송 완료 후 — 목록 갱신 + 선택 해제
  const handleReselectNoticeSent = useCallback(() => {
    setSelectedIds(new Set());
    invalidateDashboard();
  }, [invalidateDashboard]);

  return {
    // Data
    dashboardStatus,
    statistics,
    visibleRequested,
    extraPaymentPending,
    reselectPending,
    reselectCancelRequested,
    todayDelivered,
    filteredData,
    productOptions,
    chargeEstimate,
    listPriceEstimate,
    selectedChargeEstimate,
    channelBalances,

    // Filters
    productFilter,
    setProductFilter,
    searchText,
    setSearchText,

    // Selection
    selectedIds,
    isAllSelected,
    canToggleAll,
    selectedProduct,
    handleToggleAll,
    handleToggleSelect,
    setSelectedIds,
    overIssuedSelectedIds,
    deselectOverIssued,
    allowOverIssue,
    requestOverIssueOverride,

    // Deliver
    deliveringId,
    isBulkProcessing,
    openDeliverModal,
    openBulkDeliverModal,
    deliverProduct,

    // Modals
    deliverModal,
    setDeliverModal,
    handleConfirmDeliver,
    handleReject,
    handleUpdateRecipientPhone,
    historyModal,
    setHistoryModal,
    openHistoryModal,
    shareHistoryModal,
    setShareHistoryModal,
    openShareHistoryModal,

    // Today delivered
    showTodayDelivered,
    setShowTodayDelivered,

    // Bulk update delivered coupon (도매가/매체)
    handleBulkUpdateDeliveredCoupon,
    handleBulkUndeliverCoupon,
    onHoldCoupons,
    handleUnholdCoupons,

    // 품절 재선택 (안내 철회 / 발송 후 갱신)
    handleCancelReselectNotice,
    handleReselectNoticeSent,

    // 추가결제 대기 처리 (A: 추가결제 / B: 대체발송 / C: 신청취소)
    handlePayExtraPayment,
    handleFulfillFromExisting,
    handleCancelSpawned,
    getDonorCandidates,

    // Toast & clipboard
    toastMessage,
    showToast,
    toastDurationMs,
    copyToClipboard,

    // 번호 복사 (매체 발주용) — 확인 단계 + 마지막 복사 상품
    copyConfirm,
    setCopyConfirm,
    requestCopySelectedPhones,
    confirmCopySelectedPhones,
    lastCopiedProduct,
  };
}
